<nb-card #wizardCard>
  <nb-card-header class="wizard-header">
    <h2>{{ 'tutor.ltiWizard.title' | translate }}</h2>
    <button
      nbButton
      ghost
      size="small"
      class="wizard-header__close"
      [attr.aria-label]="'buttons.close' | translate"
      (click)="close()"
    >
      <nb-icon icon="close-outline"></nb-icon>
    </button>
  </nb-card-header>

  <nb-card-body>
    <!-- Phase indicator: three logical phases (Allgemein → Konfiguration
         → Abschluss). Grid columns are weighted by the step count of each
         phase so the bracket spans exactly its steps. -->
    <div
      class="wizard-phases"
      [style.grid-template-columns]="phaseGridTemplate"
    >
      <div
        *ngFor="let key of PHASE_LABEL_KEYS; let i = index"
        class="wizard-phase"
        [class.wizard-phase--active]="currentPhaseIndex === i"
        [class.wizard-phase--done]="currentPhaseIndex > i"
      >
        <span class="wizard-phase__label">{{ key | translate }}</span>
      </div>
    </div>

    <!-- Linear: step 3 PATCHes the placeholder registration created in
         step 1. Skipping step 1 would leave step 3 without a target. -->
    <nb-stepper
      #stepper
      orientation="horizontal"
      [linear]="true"
      (stepChange)="onStepChange($event)"
    >

      <!-- ──────────────────────────────────────────────────────── -->
      <!-- Step 1: Site identifizieren                              -->
      <!-- ──────────────────────────────────────────────────────── -->
      <nb-step [label]="'tutor.ltiWizard.step1.label' | translate">
        <p class="step-guidance">
          {{ 'tutor.ltiWizard.step1.hint' | translate }}
        </p>

        <form [formGroup]="step1" (ngSubmit)="submitStep1()" class="wizard-step">
          <div class="wizard-tiles">
            <div class="wizard-tile">
              <h5 class="wizard-tile__title">
                {{ 'tutor.ltiWizard.step1.tileTitle' | translate }}
              </h5>

              <div class="field">
                <label class="field__label" for="display_name">
                  {{ 'tutor.ltiWizard.step1.displayName' | translate }}
                </label>
                <input
                  id="display_name"
                  nbInput
                  fullWidth
                  formControlName="display_name"
                  placeholder="z.B. Hauptcampus Moodle"
                />
              </div>

              <div class="field">
                <label class="field__label" for="lti_issuer">
                  {{ 'tutor.ltiWizard.step1.issuer' | translate }}
                </label>
                <input
                  id="lti_issuer"
                  nbInput
                  fullWidth
                  formControlName="lti_issuer"
                  placeholder="https://moodle.example.com"
                />
                <p class="field__help">
                  {{ 'tutor.ltiWizard.step1.issuerHint' | translate }}
                </p>
              </div>
            </div>
          </div>

          <div *ngIf="errorMessage" class="wizard-error">{{ errorMessage }}</div>

          <div class="step-footer step-footer--between">
            <button nbButton type="button" status="basic" (click)="close()">
              {{ 'buttons.cancel' | translate }}
            </button>
            <button nbButton type="submit" status="primary" [disabled]="saving">
              {{ 'buttons.next' | translate }}
            </button>
          </div>
        </form>
      </nb-step>

      <!-- ──────────────────────────────────────────────────────── -->
      <!-- Step 2: Tool-URLs anzeigen + Anleitung                   -->
      <!-- ──────────────────────────────────────────────────────── -->
      <nb-step [label]="'tutor.ltiWizard.step2.label' | translate">
        <p class="step-guidance">
          {{ 'tutor.ltiWizard.step2.hint' | translate }}
        </p>

        <div class="wizard-step">
          <div class="wizard-tiles">
            <div class="wizard-tile">
              <h5 class="wizard-tile__title">
                {{ 'tutor.ltiWizard.step2.urlsTitle' | translate }}
              </h5>

              <div class="field" *ngFor="let item of [
                { key: 'toolUrl',         value: toolUrls.launch },
                { key: 'loginUrl',        value: toolUrls.login },
                { key: 'jwksUrl',         value: toolUrls.jwks },
                { key: 'deepLinkingUrl',  value: toolUrls.deepLinking }
              ]">
                <label class="field__label">
                  {{ ('tutor.ltiWizard.step2.urls.' + item.key) | translate }}
                </label>
                <div class="url-row">
                  <code class="url-row__value">{{ item.value }}</code>
                  <button
                    nbButton
                    ghost
                    size="tiny"
                    type="button"
                    [attr.aria-label]="'buttons.copy' | translate"
                    (click)="copyToClipboard(item.value, $event)"
                  >
                    <nb-icon icon="copy-outline"></nb-icon>
                  </button>
                </div>
              </div>
            </div>

            <div class="wizard-tile">
              <h5 class="wizard-tile__title">
                {{ 'tutor.ltiWizard.step2.instructionsTitle' | translate }}
              </h5>

              <ol class="wizard-steps-list">
                <li>{{ 'tutor.ltiWizard.step2.steps.s1' | translate }}</li>
                <li>{{ 'tutor.ltiWizard.step2.steps.s2' | translate }}</li>
                <li>{{ 'tutor.ltiWizard.step2.steps.s3' | translate }}</li>
                <li>{{ 'tutor.ltiWizard.step2.steps.s4' | translate }}</li>
                <li>{{ 'tutor.ltiWizard.step2.steps.s5' | translate }}</li>
              </ol>
              <p class="field__help">
                {{ 'tutor.ltiWizard.step2.privacy' | translate }}
              </p>
            </div>
          </div>
        </div>

        <div class="step-footer step-footer--between">
          <button nbButton type="button" status="basic" nbStepperPrevious>
            {{ 'buttons.back' | translate }}
          </button>
          <button nbButton type="button" status="primary" nbStepperNext>
            {{ 'buttons.next' | translate }}
          </button>
        </div>
      </nb-step>

      <!-- ──────────────────────────────────────────────────────── -->
      <!-- Step 3: IDs aus Moodle eintragen                         -->
      <!-- ──────────────────────────────────────────────────────── -->
      <nb-step [label]="'tutor.ltiWizard.step3.label' | translate">
        <p class="step-guidance">
          {{ 'tutor.ltiWizard.step3.hint' | translate }}
        </p>

        <form [formGroup]="step3" (ngSubmit)="submitStep3()" class="wizard-step">
          <div class="wizard-tiles">
            <div class="wizard-tile">
              <h5 class="wizard-tile__title">
                {{ 'tutor.ltiWizard.step3.tileTitle' | translate }}
              </h5>

              <div class="field">
                <label class="field__label" for="lti_client_id">
                  {{ 'tutor.ltiWizard.step3.clientId' | translate }}
                </label>
                <input
                  id="lti_client_id"
                  nbInput
                  fullWidth
                  formControlName="lti_client_id"
                  placeholder="z.B. abc123"
                />
                <p class="field__help">
                  {{ 'tutor.ltiWizard.step3.clientIdHint' | translate }}
                </p>
              </div>

              <div class="field">
                <label class="field__label" for="lti_deployment_id">
                  {{ 'tutor.ltiWizard.step3.deploymentId' | translate }}
                </label>
                <input
                  id="lti_deployment_id"
                  nbInput
                  fullWidth
                  formControlName="lti_deployment_id"
                  placeholder="optional"
                />
                <p class="field__help">
                  {{ 'tutor.ltiWizard.step3.deploymentIdHint' | translate }}
                </p>
              </div>
            </div>

            <div class="wizard-tile">
              <h5 class="wizard-tile__title">
                {{ 'tutor.ltiWizard.step3.endpointsTitle' | translate }}
              </h5>
              <p class="field__help">
                {{ 'tutor.ltiWizard.step3.endpointsHint' | translate }}
              </p>
              <button
                type="button"
                nbButton
                ghost
                size="small"
                status="primary"
                class="fill-moodle"
                (click)="fillMoodleEndpoints()"
              >
                {{ 'tutor.ltiWizard.step3.fillMoodle' | translate }}
              </button>

              <div class="field">
                <label class="field__label" for="lti_platform_auth_url">
                  {{ 'tutor.ltiWizard.step3.platformAuthUrl' | translate }}
                </label>
                <input
                  id="lti_platform_auth_url"
                  nbInput
                  fullWidth
                  formControlName="lti_platform_auth_url"
                  placeholder="https://lms.example.com/…/auth"
                />
              </div>

              <div class="field">
                <label class="field__label" for="lti_platform_token_url">
                  {{ 'tutor.ltiWizard.step3.platformTokenUrl' | translate }}
                </label>
                <input
                  id="lti_platform_token_url"
                  nbInput
                  fullWidth
                  formControlName="lti_platform_token_url"
                  placeholder="https://lms.example.com/…/token"
                />
              </div>

              <div class="field">
                <label class="field__label" for="lti_platform_jwks_url">
                  {{ 'tutor.ltiWizard.step3.platformJwksUrl' | translate }}
                </label>
                <input
                  id="lti_platform_jwks_url"
                  nbInput
                  fullWidth
                  formControlName="lti_platform_jwks_url"
                  placeholder="https://lms.example.com/…/keys"
                />
              </div>
            </div>
          </div>

          <div *ngIf="errorMessage" class="wizard-error">{{ errorMessage }}</div>

          <div class="step-footer step-footer--between">
            <button nbButton type="button" status="basic" nbStepperPrevious>
              {{ 'buttons.back' | translate }}
            </button>
            <button nbButton type="submit" status="primary" [disabled]="saving">
              {{ 'tutor.ltiWizard.step3.next' | translate }}
            </button>
          </div>
        </form>
      </nb-step>

      <!-- ──────────────────────────────────────────────────────── -->
      <!-- Step 4: Fertig                                            -->
      <!-- ──────────────────────────────────────────────────────── -->
      <nb-step [label]="'tutor.ltiWizard.step4.label' | translate">
        <div class="wizard-step">
          <div class="wizard-tiles">
            <div class="wizard-tile wizard-tile--done">
              <nb-icon
                icon="checkmark-circle-2-outline"
                status="success"
                class="wizard-done__icon"
              ></nb-icon>
              <h5 class="wizard-tile__title">
                {{ 'tutor.ltiWizard.step4.title' | translate }}
              </h5>
              <p class="wizard-done__body">
                {{ 'tutor.ltiWizard.step4.body' | translate }}
              </p>
            </div>
          </div>
        </div>

        <div class="step-footer step-footer--between">
          <button nbButton type="button" status="basic" (click)="close()">
            {{ 'tutor.ltiWizard.step4.closeOnly' | translate }}
          </button>
          <button nbButton type="button" status="primary" (click)="goToPlacements()">
            {{ 'tutor.ltiWizard.step4.goToPlacements' | translate }}
          </button>
        </div>
      </nb-step>

    </nb-stepper>
  </nb-card-body>
</nb-card>
